import { useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Building2,
  CalendarCheck,
  CheckCircle2,
  DollarSign,
  Eye,
  Mail,
  MapPin,
  Pause,
  Pencil,
  Phone,
  Play,
  Sparkles,
  Star,
  Users,
  Wifi,
  Coffee,
  ParkingCircle,
  Snowflake,
  Video,
  Presentation,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useDirection } from "@/hooks/use-direction";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/spaces/$id")({
  component: SpaceDetailPage,
});

type SpaceRecord = {
  id: string;
  name: string;
  location: string;
  city: string;
  merchant: string;
  merchantEmail: string;
  merchantPhone: string;
  category: string;
  rating: number;
  reviews: number;
  bookings: number;
  revenue: number;
  status: "Active" | "Inactive";
  featured?: boolean;
  cover: string;
  gallery: string[];
  capacity: number;
  price: number;
  description: string;
  amenities: { label: string; icon: typeof Wifi }[];
  createdAt: string;
};

const SPACES: Record<string, SpaceRecord> = {
  amber: {
    id: "amber",
    name: "The Amber Lounge",
    location: "Al Olaya, Riyadh",
    city: "Riyadh",
    merchant: "Khalid A.",
    merchantEmail: "khalid@amberlounge.sa",
    merchantPhone: "+966 55 123 4567",
    category: "Café & Lounge",
    rating: 4.8,
    reviews: 312,
    bookings: 1204,
    revenue: 184320,
    status: "Active",
    featured: true,
    cover: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1600&q=70",
    gallery: [
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=60",
      "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?auto=format&fit=crop&w=800&q=60",
      "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=800&q=60",
    ],
    capacity: 45,
    price: 120,
    description:
      "A warm, sun-drenched lounge in the heart of Al Olaya, blending specialty coffee culture with flexible co-working nooks. Perfect for casual meetings, creative sessions, and small events.",
    amenities: [
      { label: "High-speed WiFi", icon: Wifi },
      { label: "Espresso Bar", icon: Coffee },
      { label: "Free Parking", icon: ParkingCircle },
      { label: "Climate Control", icon: Snowflake },
      { label: "Projector", icon: Presentation },
      { label: "Video Conf.", icon: Video },
    ],
    createdAt: "March 14, 2025",
  },
};

const FALLBACK: SpaceRecord = {
  ...SPACES.amber,
  id: "unknown",
  name: "Space Details",
};

function SpaceDetailPage() {
  const { t } = useTranslation();
  const dir = useDirection();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const record = SPACES[id] ?? { ...FALLBACK, id };
  const [status, setStatus] = useState(record.status);
  const [featured, setFeatured] = useState(!!record.featured);

  const kpis = [
    { label: t("adminSpaceDetail.kpi.bookings"), value: record.bookings.toLocaleString(), icon: CalendarCheck, wrap: "bg-primary/10 text-primary" },
    { label: t("adminSpaceDetail.kpi.revenue"), value: `${record.revenue.toLocaleString()}`, icon: DollarSign, wrap: "bg-emerald-500/10 text-emerald-600" },
    { label: t("adminSpaceDetail.kpi.rating"), value: `${record.rating} / 5`, icon: Star, wrap: "bg-amber-500/10 text-amber-600" },
    { label: t("adminSpaceDetail.kpi.capacity"), value: t("adminSpaceDetail.pax", { count: record.capacity }), icon: Users, wrap: "bg-[#5958F3]/10 text-[#5958F3]" },
  ];

  return (
    <AdminShell
      title={record.name}
      subtitle={t("adminSpaceDetail.subtitle")}
      actions={
        <>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-9 rounded-xl text-muted-foreground"
          >
            <Link to="/admin/spaces">
              <ArrowLeft className={cn("h-4 w-4", dir === "rtl" && "rotate-180")} /> {t("adminSpaceDetail.back")}
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFeatured((f) => !f)}
            className={cn(
              "h-9 rounded-xl",
              featured && "border-[#5958F3]/40 bg-[#5958F3]/10 text-[#5958F3]",
            )}
          >
            <Sparkles className="h-4 w-4" /> {featured ? t("adminSpaceDetail.featured") : t("adminSpaceDetail.feature")}
          </Button>
          
          <Button
            size="sm"
            onClick={() => setStatus((s) => (s === "Active" ? "Inactive" : "Active"))}
            className={cn(
              "h-9 rounded-xl text-white",
              status === "Active"
                ? "bg-amber-500 hover:bg-amber-600"
                : "bg-emerald-500 hover:bg-emerald-600",
            )}
          >
            {status === "Active" ? (
              <>
                <Pause className="h-4 w-4" /> {t("adminSpaceDetail.pause")}
              </>
            ) : (
              <>
                <Play className="h-4 w-4" /> {t("adminSpaceDetail.activate")}
              </>
            )}
          </Button>
        </>
      }
    >
      {/* Hero */}
      <Card className="overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="relative h-64 w-full sm:h-80">
          <img
            src={record.cover}
            alt={record.name}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 start-0 end-0 flex flex-wrap items-end justify-between gap-3 p-5 text-white">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                    status === "Active"
                      ? "bg-emerald-500/20 text-emerald-100 ring-emerald-300/40"
                      : "bg-slate-500/30 text-slate-100 ring-slate-300/40",
                  )}
                >
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      status === "Active" ? "bg-emerald-300" : "bg-slate-300",
                    )}
                  />
                  {t(`adminSpaceDetail.status.${status}`)}
                </span>
                {featured && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#5958F3] px-2.5 py-0.5 text-[11px] font-semibold text-white">
                    <Sparkles className="h-3 w-3" /> {t("adminSpaceDetail.featured")}
                  </span>
                )}
                <span className="inline-flex items-center rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold backdrop-blur">
                  {record.category}
                </span>
              </div>
              <h2 className="font-display text-2xl font-bold sm:text-3xl">{record.name}</h2>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-white/85">
                <MapPin className="h-4 w-4" /> {record.location}
              </p>
            </div>
            <div className="rounded-xl bg-white/15 px-4 py-2.5 backdrop-blur">
              <p className="text-[11px] uppercase tracking-wider text-white/70">{t("adminSpaceDetail.from")}</p>
              <p className="font-display text-xl font-bold"><Riyal /> {record.price}{t("adminSpaceDetail.perHour")}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => (
          <Card
            key={k.label}
            className="rounded-2xl border-border/60 p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
          >
            <div className="flex items-start gap-4">
              <div className={cn("grid h-11 w-11 shrink-0 place-items-center rounded-xl", k.wrap)}>
                <k.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-display text-2xl font-bold tracking-tight">{k.value}</p>
                <p className="mt-0.5 text-sm font-medium text-muted-foreground">{k.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Content */}
      <div className="mt-6 grid gap-6 lg:grid-cols-[1.7fr_1fr]">
        <div className="space-y-6">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="rounded-xl">
              <TabsTrigger value="overview" className="rounded-lg">{t("adminSpaceDetail.tabs.overview")}</TabsTrigger>
              <TabsTrigger value="gallery" className="rounded-lg">{t("adminSpaceDetail.tabs.gallery")}</TabsTrigger>
              <TabsTrigger value="bookings" className="rounded-lg">{t("adminSpaceDetail.tabs.bookings")}</TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-lg">{t("adminSpaceDetail.tabs.reviews")}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
                <h3 className="font-display text-lg font-bold tracking-tight">{t("adminSpaceDetail.about")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {record.description}
                </p>
              </Card>
              <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
                <h3 className="font-display text-lg font-bold tracking-tight">{t("adminSpaceDetail.amenities")}</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {record.amenities.map((a) => (
                    <div key={a.label} className="flex items-center gap-3 rounded-xl border border-border/60 bg-surface/50 px-3 py-2.5">
                      <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#5958F3]/10 text-[#5958F3]">
                        <a.icon className="h-4 w-4" />
                      </span>
                      <span className="text-sm font-medium">{t(`adminSpaceDetail.amenity.${a.label}`, { defaultValue: a.label })}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="gallery" className="mt-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {record.gallery.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`${record.name} photo ${i + 1}`}
                    className="h-44 w-full rounded-2xl object-cover shadow-soft"
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="bookings" className="mt-4">
              <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
                <h3 className="font-display text-lg font-bold tracking-tight">{t("adminSpaceDetail.recentBookings")}</h3>
                <div className="mt-4 divide-y divide-border/60">
                  {[
                    { user: "Nora F.", date: "Mar 20, 2026 · 2:00 PM", hrs: 3, total: 360, status: "Completed" },
                    { user: "Ahmed S.", date: "Mar 18, 2026 · 10:00 AM", hrs: 5, total: 600, status: "Completed" },
                    { user: "Lina H.", date: "Mar 15, 2026 · 6:00 PM", hrs: 2, total: 240, status: "Refunded" },
                  ].map((b, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-3 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="text-[11px]">
                            {b.user.split(" ").map((p) => p[0]).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-semibold">{b.user}</p>
                          <p className="text-xs text-muted-foreground">{b.date} · {t("adminSpaceDetail.hours", { count: b.hrs })}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold"><Riyal /> {b.total}</span>
                        <span className={cn(
                          "rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                          b.status === "Completed"
                            ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20"
                            : "bg-rose-500/10 text-rose-700 ring-rose-500/20",
                        )}>
                          {t(`adminSpaceDetail.bookingStatus.${b.status}`)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="reviews" className="mt-4">
              <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
                <h3 className="font-display text-lg font-bold tracking-tight">{t("adminSpaceDetail.guestReviews")}</h3>
                <div className="mt-4 space-y-4">
                  {[
                    { user: "Mona T.", rating: 5, text: "Beautiful space, great coffee, super productive vibe." },
                    { user: "Ziad R.", rating: 4, text: "Loved the design. Could use a bit more parking on weekends." },
                    { user: "Hala D.", rating: 5, text: "Perfect spot for a small team offsite." },
                  ].map((r, i) => (
                    <div key={i} className="rounded-xl border border-border/60 p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold">{r.user}</p>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold">
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {r.rating}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm text-muted-foreground">{r.text}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <h3 className="font-display text-base font-bold tracking-tight">{t("adminSpaceDetail.merchant")}</h3>
            <div className="mt-3 flex items-center gap-3">
              <Avatar className="h-11 w-11">
                <AvatarFallback>
                  {record.merchant.split(" ").map((p) => p[0]).join("")}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{record.merchant}</p>
                <p className="truncate text-xs text-muted-foreground">{t("adminSpaceDetail.spaceOwner")}</p>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <a href={`mailto:${record.merchantEmail}`} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <Mail className="h-4 w-4" /> {record.merchantEmail}
              </a>
              <a href={`tel:${record.merchantPhone}`} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <Phone className="h-4 w-4" /> {record.merchantPhone}
              </a>
            </div>
            <Button asChild variant="outline" size="sm" className="mt-4 w-full rounded-xl">
              <a href="/companies/loftworks">
                <Eye className="h-4 w-4" /> {t("adminSpaceDetail.viewMerchant")}
              </a>
            </Button>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <h3 className="font-display text-base font-bold tracking-tight">{t("adminSpaceDetail.details")}</h3>
            <dl className="mt-3 space-y-2.5 text-sm">
              {[
                { k: t("adminSpaceDetail.detail.id"), v: record.id },
                { k: t("adminSpaceDetail.detail.category"), v: record.category },
                { k: t("adminSpaceDetail.detail.city"), v: t(`adminSpaces.cities.${record.city}`, { defaultValue: record.city }) },
                { k: t("adminSpaceDetail.detail.capacity"), v: t("adminSpaceDetail.people", { count: record.capacity }) },
                { k: t("adminSpaceDetail.detail.rate"), v: <span className="inline-flex items-center gap-1"><Riyal /> {record.price}</span> },
                { k: t("adminSpaceDetail.detail.listed"), v: record.createdAt },
              ].map((r) => (
                <div key={r.k} className="flex items-center justify-between gap-3">
                  <dt className="text-muted-foreground">{r.k}</dt>
                  <dd className="font-medium">{r.v}</dd>
                </div>
              ))}
            </dl>
          </Card>

          <Card className="rounded-2xl border-none p-5 text-white shadow-soft" style={{ backgroundColor: "#5958F3" }}>
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/15">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t("adminSpaceDetail.quality.title")}</p>
                <p className="text-[11px] text-white/80">{t("adminSpaceDetail.quality.sub")}</p>
              </div>
            </div>
            <p className="mt-4 font-display text-4xl font-bold">96<span className="text-xl text-white/70">/100</span></p>
            <p className="mt-1 text-xs text-white/80">{t("adminSpaceDetail.quality.note")}</p>
          </Card>

          <Button
            variant="outline"
            className="w-full rounded-xl"
            onClick={() => navigate({ to: "/admin/spaces" })}
          >
            <Building2 className="h-4 w-4" /> {t("adminSpaceDetail.backToAll")}
          </Button>
        </div>
      </div>
    </AdminShell>
  );
}
